import { useQuery } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FieldArray, Formik } from "formik";
import { DateTime } from "luxon";
import { enqueueSnackbar, useSnackbar } from "notistack";
import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { staffTollFreeRegistrationSchema } from "@stw/yup-validators";

import {
  GetTollFreeRegistrationQuery,
  EditTollFreeRegistrationMutation,
  EditTollFreeRegistrationMutationVariables,
  SubmitTollFreeRegistrationToBandwidthMutation,
  SubmitTollFreeRegistrationToBandwidthMutationVariables,
  TollFreeOrgType,
  TollFreeSubmissionStatus,
  CopyTollFreeRegistrationMutation,
  CopyTollFreeRegistrationMutationVariables,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  FormikDatePicker,
  FormikErrorBanner,
  FormikSelect,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import {
  COPY_TOLL_FREE_REGISTRATION,
  EDIT_TOLL_FREE_REGISTRATION,
  GET_TOLL_FREE_REGISTRATION,
  SUBMIT_TOLL_FREE_REGISTRATION_TO_BANDWIDTH,
} from "~frontend/graphql/tollFreeRegistrations";
import { InputRow } from "~frontend/routes/staff/compliance-registrations/ten-dlc/TenDlcProvider";
import useOptOutPhraseList from "~frontend/routes/subaccounts/campaignEditor/Script/useOptOutPhraseList";
import { COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS } from "~frontend/routes/subaccounts/phoneNumberList/TollFreeRegistration";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";

import { StaffTollFreePhoneNumberList } from "./StaffTollFreePhoneNumberList";

const ORG_TYPE_MENU_OPTIONS: { label: string; value: TollFreeOrgType }[] = [
  {
    label: "Political",
    value: TollFreeOrgType.Political,
  },
  {
    label: "501c3",
    value: TollFreeOrgType.C3,
  },
  {
    label: "501c4",
    value: TollFreeOrgType.C4,
  },
  {
    label: "501c5",
    value: TollFreeOrgType.C5,
  },
  {
    label: "Private Entity",
    value: TollFreeOrgType.Private,
  },
  {
    label: "Government",
    value: TollFreeOrgType.Government,
  },
];

const USE_CASES: string[] = [
  "Fundraising",
  "Political",
  "Voting Reminders",
  "Mixed",
  "2FA",
  "App Notifications",
  "Appointments",
  "Auctions",
  "Auto Repair Services",
  "Bank Transfers",
  "Billing",
  "Booking Confirmations",
  "Business Updates",
  "Career Training",
  "Chatbot",
  "Conversational / Alerts",
  "Courier Services & Deliveries",
  "COVID-19 Alerts",
  "Emergency Alerts",
  "Events & Planning",
  "Financial Services",
  "Fraud Alerts",
  "General Marketing",
  "General School Updates",
  "Healthcare Alerts",
  "Housing Community Updates",
  "HR / Staffing",
  "Insurance Services",
  "Job Dispatch",
  "Legal Services",
  "Motivational Reminders",
  "Notary Notifications",
  "Order Notifications",
  "Public Works",
  "Real Estate Services",
  "Religious Services",
  "Repair and Diagnostics Alerts",
  "Rewards Program",
  "Surveys",
  "System Alerts",
  "Waitlist Alerts",
  "Webinar Reminders",
  "Workshop Alerts",
];

function BandwidthSubmissionControls({
  submissionStatus,
  disabled,
  onSubmit,
}: {
  submissionStatus: TollFreeSubmissionStatus;
  disabled: boolean;
  onSubmit: () => void;
}): ReactNode {
  switch (submissionStatus) {
    case TollFreeSubmissionStatus.NotSubmitted:
      return (
        <Button
          variant="contained"
          disabled={
            disabled ||
            submissionStatus !== TollFreeSubmissionStatus.NotSubmitted
          }
          onClick={onSubmit}
        >
          Submit to Bandwidth
        </Button>
      );
    case TollFreeSubmissionStatus.Pending:
      return <Typography>Bandwidth submission pending.</Typography>;
    case TollFreeSubmissionStatus.Verified:
      return <Typography>Verification complete.</Typography>;
    case TollFreeSubmissionStatus.Failed:
      // TODO: resubmit if allowed
      return <Typography>Verification failed.</Typography>;
    default:
      return null as never;
  }
}

export const StaffTollFreeRegistration = (): ReactNode => {
  const { tollFreeRegistrationId } = useParams<{
    tollFreeRegistrationId: string;
  }>();
  const snackbar = useSnackbar();

  const { phraseList: optOutPhrases, loading: optOutPhrasesLoading } =
    useOptOutPhraseList();

  const { loading, data, error } = useQuery<GetTollFreeRegistrationQuery>(
    GET_TOLL_FREE_REGISTRATION,
    {
      variables: {
        input: {
          registrationId: tollFreeRegistrationId!,
        },
      },
    }
  );

  const [editTollFreeRegistration] = useMutation<
    EditTollFreeRegistrationMutation,
    EditTollFreeRegistrationMutationVariables
  >(EDIT_TOLL_FREE_REGISTRATION, {
    onErrorSnackbarMessage: "Failed to save changes to toll free registration",
    refetchQueries: [GET_TOLL_FREE_REGISTRATION],
  });

  const [submitTollFreeRegistrationToBandwidth] = useMutation<
    SubmitTollFreeRegistrationToBandwidthMutation,
    SubmitTollFreeRegistrationToBandwidthMutationVariables
  >(SUBMIT_TOLL_FREE_REGISTRATION_TO_BANDWIDTH, {
    onErrorSnackbarMessage:
      "Failed to submit toll-free registration to Bandwidth",
    refetchQueries: [GET_TOLL_FREE_REGISTRATION],
  });

  const [copyTollFreeRegistration] = useMutation<
    CopyTollFreeRegistrationMutation,
    CopyTollFreeRegistrationMutationVariables
  >(COPY_TOLL_FREE_REGISTRATION, {
    onErrorSnackbarMessage: "Error copying registration",
    variables: {
      input: {
        registrationId: tollFreeRegistrationId!,
      },
    },
  });

  const navigate = useNavigate();

  const handleCopyRegistration = async (registrationId: string) => {
    const { data, errors } = await copyTollFreeRegistration({
      variables: {
        input: { registrationId: registrationId },
      },
    });
    if (!errors) {
      const newRegistrationId = data?.copyTollFreeRegistration?.id || "";
      enqueueSnackbar("Data copied to new registration", {
        variant: "success",
      });
      await navigate(
        `/staff/compliance-registrations/toll-free/${newRegistrationId}`
      );
    }
  };

  return (
    // TODO: distinguish informational fields
    <PageContainer>
      <PageHeader>Toll-Free Registration</PageHeader>
      <AccentBox>
        <AsyncWrapper
          data={data}
          loading={[loading, optOutPhrasesLoading]}
          errors={[error]}
        >
          {({ getTollFreeRegistration }) => (
            <FlexBox direction="column" gap={2}>
              {getTollFreeRegistration.submissionStatus !==
                TollFreeSubmissionStatus.NotSubmitted && (
                <StaffTollFreePhoneNumberList
                  numbers={getTollFreeRegistration.inventoryPhoneNumbers}
                />
              )}

              <FlexBox direction="column">
                <Typography variant="h2Bold" component="div" sx={{ pb: 1 }}>
                  Registration Details
                </Typography>

                <TwoColumnDisplay
                  rows={[
                    {
                      key: "tenant-slug",
                      label: "Tenant slug",
                      content: getTollFreeRegistration.tenant.slug,
                    },
                    {
                      key: "subaccount-slug",
                      label: "Subaccount slug",
                      content: getTollFreeRegistration.subaccount.slug,
                    },
                  ]}
                />

                <Formik
                  validateOnBlur
                  validateOnMount={true}
                  initialTouched={Object.keys(getTollFreeRegistration).reduce(
                    (a, c) => {
                      return { ...a, [c]: true };
                    },
                    {}
                  )}
                  validationSchema={staffTollFreeRegistrationSchema(
                    optOutPhrases!
                  )}
                  initialValues={{
                    organizationName: getTollFreeRegistration.organizationName,
                    orgType: getTollFreeRegistration.orgType,
                    displayName: getTollFreeRegistration.displayName,
                    ein: getTollFreeRegistration.ein,
                    cvToken: getTollFreeRegistration.cvToken,
                    address1: getTollFreeRegistration.address1,
                    address2: getTollFreeRegistration.address2,
                    city: getTollFreeRegistration.city,
                    state: getTollFreeRegistration.state,
                    postalCode: getTollFreeRegistration.postalCode,
                    country: getTollFreeRegistration.country,
                    website: getTollFreeRegistration.website,

                    contactFirstName: getTollFreeRegistration.contactFirstName,
                    contactLastName: getTollFreeRegistration.contactLastName,
                    contactEmail: getTollFreeRegistration.contactEmail,
                    contactPhone: getTollFreeRegistration.contactPhone,

                    orgContactIsPrimaryContact:
                      getTollFreeRegistration.orgContactIsPrimaryContact,

                    primaryContactFirstName:
                      getTollFreeRegistration.primaryContactFirstName,
                    primaryContactLastName:
                      getTollFreeRegistration.primaryContactLastName,
                    primaryContactEmail:
                      getTollFreeRegistration.primaryContactEmail,
                    primaryContactPhone:
                      getTollFreeRegistration.primaryContactPhone,

                    notes: getTollFreeRegistration.notes,
                    requestedTnCount:
                      getTollFreeRegistration.requestedTnCount || 5,
                    additionalContext:
                      getTollFreeRegistration.additionalContext,
                    usageStartDate: getTollFreeRegistration.usageStartDate
                      ? DateTime.fromMillis(
                          getTollFreeRegistration.usageStartDate
                        )
                      : null,

                    messageVolume: getTollFreeRegistration.messageVolume,
                    useCaseSummary: getTollFreeRegistration.useCaseSummary,
                    useCase: getTollFreeRegistration.useCase,
                    productionMessageContent:
                      getTollFreeRegistration.productionMessageContent,
                    usageTypes: getTollFreeRegistration.usageTypes || [],
                    optInWorkflowDescription:
                      getTollFreeRegistration.optInWorkflowDescription,
                    optInWorkflowImageUrls:
                      getTollFreeRegistration.optInWorkflowImageUrls || [],
                    createComplianceBundle:
                      getTollFreeRegistration.createComplianceBundle,
                    privacyPolicyUrl: getTollFreeRegistration.privacyPolicyUrl,
                    optInPageUrl: getTollFreeRegistration.optInPageUrl,
                    termsAndConditionsUrl:
                      getTollFreeRegistration.termsAndConditionsUrl,

                    [submitErrorField]: null,
                  }}
                  onSubmit={async (
                    { _submitError, ...values },
                    { setFieldError, resetForm }
                  ) => {
                    // optimistic update to make sure RouteLeavingGuard
                    // doesn't trigger if the submit succeeds
                    resetForm({
                      values: {
                        ...values,
                        [submitErrorField]: null,
                      },
                    });
                    const { errors } = await editTollFreeRegistration({
                      variables: {
                        input: {
                          ...values,
                          requestedTnCount: Number(
                            values.requestedTnCount || 0
                          ),
                          registrationId: tollFreeRegistrationId!,
                          tenantId: getTollFreeRegistration.tenant.id,
                        },
                      },
                    });

                    if (errors) {
                      // for RouteLeavingGuard
                      setFieldError(submitErrorField, errors[0]?.message);
                    } else {
                      snackbar.enqueueSnackbar("Changes saved", {
                        variant: "success",
                      });
                    }
                  }}
                >
                  {({
                    touched,
                    errors,
                    values,
                    dirty,
                    isSubmitting,
                    isValid,
                    submitForm,
                    setErrors,
                    setFieldValue,
                    setFieldTouched,
                    validateForm,
                    handleBlur,
                  }) => (
                    <>
                      <RouteLeavingGuard when={dirty} />
                      <FlexBox flexDirection="column" sx={{ gap: 1 }}>
                        <FormikErrorBanner />

                        <InputRow>
                          <FormikTextField
                            fast
                            label="Organization Name"
                            name="organizationName"
                          />
                          <FormikSelect
                            label="Organization Type"
                            name="orgType"
                            withNoneOption
                          >
                            {ORG_TYPE_MENU_OPTIONS.map(type => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        </InputRow>
                        <InputRow>
                          <FormikTextField
                            fast
                            label="Doing Business As (DBA)"
                            name="displayName"
                          />
                          <FormikTextField
                            label={"EIN"}
                            name="ein"
                            fast
                            onBlur={async e => {
                              const numsOnly = e.target.value.replace("-", "");
                              const newValue = numsOnly
                                ? `${numsOnly.slice(0, 2)}-${numsOnly.slice(2)}`
                                : "";
                              await setFieldValue("ein", newValue);
                            }}
                          />
                        </InputRow>
                        {values.orgType === "POLITICAL" && (
                          <InputRow>
                            <FormikTextField
                              label={"Campaign Verify Token"}
                              name="cvToken"
                              fast
                              multiline
                              minRows={3}
                              onBlur={async e => {
                                await setFieldValue(
                                  "cvToken",
                                  e.target.value.trim()
                                );
                                handleBlur(e);
                              }}
                            />
                            <Typography
                              component="div"
                              sx={{
                                width: "48%",
                                alignSelf: "center",
                              }}
                            >
                              527s (political committees) must have an identity
                              token from Campaign Verify to apply.{" "}
                              <ExternalLink href="https://support.scaletowin.com/campaign-verify-registration-guide">
                                Click here to learn more about how to get
                                verified through Campaign Verify.
                              </ExternalLink>
                            </Typography>
                          </InputRow>
                        )}
                        <InputRow>
                          <FormikTextField
                            fast
                            label="Number of toll-free numbers to purchase"
                            name="requestedTnCount"
                          />
                          <div />
                        </InputRow>

                        <InputRow>
                          <FormikTextField
                            label="Street One"
                            name="address1"
                            fast
                          />
                          <FormikTextField
                            label="Street Two"
                            name="address2"
                            fast
                          />
                        </InputRow>

                        <FlexBox gap={4} sx={{ width: "100%" }}>
                          <FormikTextField
                            label="City"
                            name="city"
                            fast
                            fullWidth
                            sx={{ flexGrow: 1 }}
                          />
                          <FormikSelect
                            label="State"
                            name="state"
                            fullWidth
                            fast
                            sx={{ flexGrow: 1 }}
                          >
                            {STATE_MENU_OPTIONS.map(state => (
                              <MenuItem key={state.value} value={state.value}>
                                {state.label}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                          <FormikTextField
                            label="Postal Code"
                            name="postalCode"
                            fast
                            fullWidth
                            sx={{ flexGrow: 1 }}
                          />
                        </FlexBox>

                        <Divider sx={{ pt: 1, mb: 2 }} />

                        <InputRow>
                          <FormikTextField
                            fast
                            name="website"
                            label="Website"
                          />
                          <FormikSelect fast name="useCase" label="Use case">
                            {USE_CASES.map(useCase => (
                              <MenuItem key={useCase} value={useCase}>
                                {useCase}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        </InputRow>
                        <Divider sx={{ pt: 1, mb: 2 }} />
                        <Typography
                          variant="h2Bold"
                          component="div"
                          sx={{ pb: 1, fontWeight: 600 }}
                        >
                          What kinds of messages will you send?
                        </Typography>

                        <InputRow>
                          <FormikTextField
                            fast
                            name="useCaseSummary"
                            label="Description"
                            helperText="Please describe your use case and the purpose of your
                      messages (i.e. political, fundraising, event reminders,
                      etc.)"
                            multiline
                          />
                        </InputRow>

                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled
                              name="createComplianceBundle"
                              value={values.createComplianceBundle}
                              checked={values.createComplianceBundle}
                              onChange={async e => {
                                await setFieldValue(
                                  "createComplianceBundle",
                                  !!e.target.checked
                                );
                              }}
                            />
                          }
                          label="Requested compliance bundle?"
                        />

                        <InputRow>
                          <FormikTextField
                            label="Privacy Policy URL"
                            name="privacyPolicyUrl"
                            fast
                          />

                          <FormikTextField
                            label="Opt In Page URL"
                            name="optInPageUrl"
                            fast
                          />
                        </InputRow>

                        <InputRow>
                          <FormikTextField
                            label="Terms and Conditions URL"
                            name="termsAndConditionsUrl"
                            fast
                          />

                          <FormikTextField
                            fast
                            name="optInWorkflowDescription"
                            label="Opt in workflow description"
                          />
                        </InputRow>

                        <InputRow>
                          <FormikTextField
                            fast
                            label="Additional context"
                            name="additionalContext"
                            multiline
                          />
                        </InputRow>

                        <InputRow>
                          <FieldArray
                            name="optInWorkflowImageUrls"
                            render={arrayHelpers => (
                              <Box sx={{ background: palette.lightGray, p: 1 }}>
                                <Typography variant="body1Bold">
                                  Opt In Workflow Image URLs
                                </Typography>
                                <Button onClick={() => arrayHelpers.push("")}>
                                  Add URL
                                </Button>
                                {values.optInWorkflowImageUrls.map(
                                  (_v: string, i: number) => (
                                    <InputRow>
                                      <FormikTextField
                                        fast
                                        name={`optInWorkflowImageUrls.${i}`}
                                      />
                                      <IconButton
                                        onClick={() => {
                                          arrayHelpers.remove(i);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </InputRow>
                                  )
                                )}
                              </Box>
                            )}
                          />
                        </InputRow>

                        <Box>
                          <InputRow>
                            <FormikTextField
                              fast
                              name="productionMessageContent"
                              label="Sample messages"
                              multiline
                            />
                          </InputRow>
                        </Box>

                        <InputRow>
                          <Box>
                            <Typography
                              variant="h2"
                              component="div"
                              sx={{ pb: 1 }}
                            >
                              Where will you use these toll-free numbers?
                            </Typography>
                            <FormGroup row>
                              {COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS.map(
                                option => (
                                  <FormControlLabel
                                    key={option.label}
                                    control={
                                      <Checkbox
                                        name={`usageType-${option.value}`}
                                        checked={values.usageTypes.includes(
                                          option.value
                                        )}
                                        onChange={async e => {
                                          if (e.target.checked) {
                                            await setFieldValue("usageTypes", [
                                              ...values.usageTypes,
                                              option.value,
                                            ]);
                                          } else {
                                            await setFieldValue(
                                              "usageTypes",
                                              values.usageTypes.filter(
                                                type => type !== option.value
                                              )
                                            );
                                          }
                                        }}
                                      />
                                    }
                                    label={option.label}
                                  />
                                )
                              )}
                            </FormGroup>
                            {touched.usageTypes && errors.usageTypes && (
                              <Typography sx={{ color: palette.red }}>
                                {errors.usageTypes as string}
                              </Typography>
                            )}
                          </Box>

                          <FormikSelect
                            label="Expected Texts per Month"
                            name="messageVolume"
                            withNoneOption
                            noneOptionLabel="Expected texts..."
                          >
                            {[
                              100, 1000, 10000, 100000, 250000, 500000, 750000,
                              1000000, 5000000, 10000000,
                            ].map(n => (
                              <MenuItem key={n} value={n}>
                                {n.toLocaleString()}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        </InputRow>

                        <InputRow>
                          <Box>
                            <Typography
                              variant="h2Bold"
                              sx={{ fontWeight: 600 }}
                            >
                              Anticipated Date of Use
                            </Typography>
                            <Typography
                              variant="h2"
                              component="div"
                              sx={{ py: 1 }}
                            >
                              When will you start using these numbers?
                            </Typography>
                            <InputRow>
                              <FormikDatePicker
                                label="Requested Start Date"
                                name="usageStartDate"
                                fast
                                inputLabelProps={{
                                  sx: {
                                    marginBottom: "-.25rem",
                                    marginTop: ".5rem",
                                  },
                                }}
                                fullWidth
                                helperTextProps={{
                                  sx: {
                                    marginLeft: "14px",
                                    fontSize: ".75xem",
                                  },
                                }}
                                helperText="Start date must be more than two business days in the future"
                              />
                            </InputRow>
                          </Box>
                        </InputRow>

                        <Divider sx={{ pt: 1, mb: 2 }} />
                        <Typography
                          variant="h2Bold"
                          component="div"
                          sx={{ pb: 1, fontWeight: 600 }}
                        >
                          Contact Details
                        </Typography>

                        <InputRow>
                          <FormikTextField
                            fast
                            label="Organization Contact First Name"
                            name="contactFirstName"
                          />
                          <FormikTextField
                            fast
                            label="Organization Contact Last Name"
                            name="contactLastName"
                          />
                        </InputRow>

                        <InputRow>
                          <FormikTextField
                            fast
                            label="Organization Contact Email"
                            name="contactEmail"
                            type="email"
                          />

                          <FormikTextField
                            fast
                            label="Organization Contact Phone"
                            name="contactPhone"
                            type="phone"
                          />
                        </InputRow>

                        <InputRow>
                          <Typography variant="h2">
                            Is the organization contact the person Scale to Win
                            should reach out to with questions about this
                            registration?
                          </Typography>

                          <RadioGroup
                            row={true}
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                            }}
                            value={values.orgContactIsPrimaryContact}
                            onChange={async e => {
                              await setFieldValue(
                                "orgContactIsPrimaryContact",
                                e.target.value === "true"
                              );
                            }}
                            name="orgContactIsPrimaryContact"
                          >
                            <FormControlLabel
                              value={true}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value={false}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </InputRow>
                        {values.orgContactIsPrimaryContact ? null : (
                          <>
                            <InputRow>
                              <FormikTextField
                                fast
                                label="Primary Contact First Name"
                                name="primaryContactFirstName"
                              />
                              <FormikTextField
                                fast
                                label="Primary Contact Last Name"
                                name="primaryContactLastName"
                              />
                            </InputRow>
                            <InputRow>
                              <FormikTextField
                                fast
                                label="Primary Contact Email"
                                name="primaryContactEmail"
                                type="email"
                              />

                              <FormikTextField
                                fast
                                label="Primary Contact Phone"
                                name="primaryContactPhone"
                                type="phone"
                              />
                            </InputRow>
                          </>
                        )}

                        <InputRow>
                          <FormikTextField
                            fast
                            label="Client notes"
                            name="notes"
                            multiline
                          />
                        </InputRow>

                        <InputRow>
                          <FlexBox sx={{ justifyContent: "space-between" }}>
                            <FlexBox sx={{ gap: "2em" }}>
                              <Button
                                variant="contained"
                                disabled={isSubmitting}
                                onClick={submitForm}
                              >
                                Save changes
                              </Button>

                              <BandwidthSubmissionControls
                                submissionStatus={
                                  getTollFreeRegistration.submissionStatus
                                }
                                disabled={dirty}
                                onSubmit={async () => {
                                  await submitTollFreeRegistrationToBandwidth({
                                    variables: {
                                      input: {
                                        registrationId:
                                          getTollFreeRegistration.id,
                                        tenantId:
                                          getTollFreeRegistration.tenant.id,
                                      },
                                    },
                                  });
                                }}
                              />
                            </FlexBox>
                            <Button
                              variant="outlined"
                              disabled={isSubmitting || dirty || !isValid}
                              onClick={async () => {
                                const formikErrors = await validateForm();
                                if (Object.keys(formikErrors).length > 0) {
                                  setErrors(formikErrors);
                                  Object.keys(formikErrors).forEach(
                                    async field =>
                                      await setFieldTouched(field, true)
                                  );
                                  enqueueSnackbar(
                                    "Please fix the errors before copying the campaign",
                                    {
                                      variant: "error",
                                    }
                                  );
                                  return;
                                }
                                await handleCopyRegistration(
                                  tollFreeRegistrationId!
                                );
                              }}
                            >
                              Copy to new
                            </Button>
                          </FlexBox>
                        </InputRow>
                      </FlexBox>
                    </>
                  )}
                </Formik>
              </FlexBox>
            </FlexBox>
          )}
        </AsyncWrapper>
      </AccentBox>
    </PageContainer>
  );
};
